import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Flex, Typography } from 'antd';
import './RolesAndPermissions.css';
import { CustomTitle } from '@/components';
import { ChevronDownIcon, ChevronUpIcon } from '@/assets/icons/icons';

const { Text } = Typography;

type KpiItem = {
  id: string;
  label: string;
};

type KpiGroup = {
  id: string;
  label: string;
  items: KpiItem[];
};

export type KpiSelectionPayload = {
  feature_name: 'home_kpi_charts';
  allowed_statuses: string[];
  meta?: {
    selected_count: number;
    total_count: number;
  };
};

type Props = {
  groups: KpiGroup[];
  selectAllByDefault?: boolean;
  initialSelectedIds?: string[];
  onApply: (payload: KpiSelectionPayload) => void;
  onReset?: () => void;
  maxHeight?: string;
};

const PARENT_ONLY_IDS = new Set([
  'alerts',
  'transport_provider_exceptions',
  'invoice_disputes',
  'cost',
  'freight_markup',
  'sku_volume',
]);

export type KpiParentOnlyInput = {
  id: string;
  items?: unknown[] | null;
};

export const isKpiParentOnly = (g: KpiParentOnlyInput): boolean =>
  PARENT_ONLY_IDS.has(g.id) && (g.items?.length ?? 0) === 0;

const KpiChartsSelector: React.FC<Props> = ({
  groups,
  selectAllByDefault = true,
  initialSelectedIds = [],
  onApply,
  onReset,
  maxHeight = '500px',
}) => {
  const didInit = useRef(false);

  const parentOnlyIds = useMemo(
    () => groups.filter(isKpiParentOnly).map((g) => g.id),
    [groups]
  );

  const allSelectableIds = useMemo(
    () => [
      ...parentOnlyIds,
      ...groups.flatMap((g) => g.items.map((i) => i.id)),
    ],
    [groups, parentOnlyIds]
  );

  const totalCount = allSelectableIds.length;

  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (didInit.current) return;

    const hasExplicit = initialSelectedIds != null;

    const ids = hasExplicit
      ? initialSelectedIds
      : selectAllByDefault && totalCount > 0
        ? allSelectableIds
        : [];

    setSelectedIds(ids ?? []);
    didInit.current = true;
  }, [allSelectableIds, initialSelectedIds, selectAllByDefault, totalCount]);

  useEffect(() => {
    if (!didInit.current) return;

    const hasExplicit = initialSelectedIds != null;

    const next = hasExplicit
      ? initialSelectedIds
      : selectAllByDefault && allSelectableIds.length > 0
        ? allSelectableIds
        : [];

    setSelectedIds(next ?? []);
  }, [initialSelectedIds, selectAllByDefault, allSelectableIds]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedCount = selectedIds.length;

  const selectAllChecked = totalCount > 0 && selectedCount === totalCount;

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? allSelectableIds : []);
  };

  const isParentOnly = (g: KpiGroup) => isKpiParentOnly(g);

  const groupState = (g: KpiGroup) => {
    if (isParentOnly(g)) {
      const checked = selectedSet.has(g.id);
      return { checked, indeterminate: false, count: 1 };
    }

    const ids = g.items.map((i) => i.id);
    const sel = ids.filter((id) => selectedSet.has(id)).length;

    return {
      checked: ids.length > 0 && sel === ids.length,
      indeterminate: sel > 0 && sel < ids.length,
      count: ids.length,
    };
  };

  const toggleGroup = (g: KpiGroup, checked: boolean) => {
    if (isParentOnly(g)) {
      setSelectedIds((prev) => {
        const s = new Set(prev);
        if (checked) s.add(g.id);
        else s.delete(g.id);
        return Array.from(s);
      });
      return;
    }

    const ids = g.items.map((i) => i.id);

    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (checked) ids.forEach((id) => s.add(id));
      else ids.forEach((id) => s.delete(id));
      return Array.from(s);
    });
  };

  const toggleItem = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (checked) s.add(id);
      else s.delete(id);
      return Array.from(s);
    });
  };

  const toggleCollapse = (groupId: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleReset = () => {
    const next = selectAllByDefault ? allSelectableIds : [];
    setSelectedIds(next);
    onReset?.();
  };

  const handleApply = () => {
    onApply({
      feature_name: 'home_kpi_charts',
      allowed_statuses: selectedIds,
      meta: {
        selected_count: selectedIds.length,
        total_count: totalCount,
      },
    });
  };

  return (
    <div className='kpiSelector' style={{ maxHeight }}>
      <div className='kpiSelector__header'>
        <CustomTitle
          text='Viewable Home KPI charts'
          fontWeight={600}
          fontSize={12}
          lineHeight='16px'
          color='var(--text-secondary)'
          marginBottom={0}
          marginTop={0}
        />
      </div>

      <div className='kpiSelector__content'>
        <div className='kpiSelector__contentInner'>
          <Flex align='center' className='kpiSelector__row'>
            <Checkbox
              className='custom-dropdown-export-coc-checkbox'
              checked={selectAllChecked}
              onChange={(e) => toggleSelectAll(e.target.checked)}
            />
            <Text className='kpiSelector__labelStrong'>Select all</Text>
          </Flex>

          {groups.map((g) => {
            const st = groupState(g);
            const isCollapsed = !!collapsedGroups[g.id];

            return (
              <React.Fragment key={g.id}>
                <Flex
                  align='center'
                  justify='space-between'
                  className='kpiSelector__row'
                >
                  <Flex align='center' gap={8}>
                    <Checkbox
                      className='custom-dropdown-export-coc-checkbox'
                      checked={st.checked}
                      // indeterminate={st.indeterminate}
                      onChange={(e) => toggleGroup(g, e.target.checked)}
                    />
                    <Text className='kpiSelector__labelStrong'>
                      {g.label}
                      {!isParentOnly(g) ? ` (${st.count})` : ''}
                    </Text>
                  </Flex>

                  {g.items.length > 0 && (
                    <span
                      className='kpiSelector__chevron'
                      onClick={() => toggleCollapse(g.id)}
                      role='button'
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                          toggleCollapse(g.id);
                      }}
                    >
                      {isCollapsed ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </span>
                  )}
                </Flex>

                {!isCollapsed && g.items.length > 0 && (
                  <div className='kpiSelector__items'>
                    {g.items.map((it) => (
                      <Flex
                        key={it.id}
                        align='center'
                        className='kpiSelector__itemRow'
                      >
                        <Checkbox
                          className='custom-dropdown-export-coc-checkbox'
                          checked={selectedSet.has(it.id)}
                          onChange={(e) => toggleItem(it.id, e.target.checked)}
                        />
                        <Text className='kpiSelector__label'>{it.label}</Text>
                      </Flex>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className='kpiSelector__actions'>
        <button
          type='button'
          onClick={handleReset}
          className='kpiSelector__btn kpiSelector__btn--ghost'
        >
          Reset
        </button>
        <button
          type='button'
          onClick={handleApply}
          className='kpiSelector__btn kpiSelector__btn--primary'
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default KpiChartsSelector;
